import * as angular from "angular";
import {IController} from "angular";
import {DraftAccountData} from "../../models/draft-account-data.model";

export class KpiContentComponent implements IController {

  selectedSection: string;
  accountData: DraftAccountData[];
  solutionData: any;
  routesCapacityData: any[] = [];
  routesTimeData: any[] = [];
  retripsData: any[] = [];
  complianceData: any[] = [];
  emptyMileageData: any[] = [];
  tripDensityData: any[] = [];
  globalData: any = {};
  summaryData: any[] = [];

  constructor() {}

  $onInit() {
  }

  $onChanges(changes: any) {
    if (changes.solutionData && this.solutionData) {
      this.processRoutesCapacityData();
      this.processRoutesTimeData();
      this.processRetripsData();
      this.processComplianceData();
      this.processEmptyMileageData();
      this.processTripDensityData();
      this.processGlobalViewData();
      this.processMeasurementSummaryData();
    }
  }

  private processRoutesCapacityData() {
    this.routesCapacityData = [];

    if (!this.solutionData || !this.solutionData.solution.problemSolvedList) {
      return;
    }

    // Iterate through problemSolvedList
    this.solutionData.solution.problemSolvedList.forEach((problem: any, problemIndex: number) => {
        const vehicleId = problem.vehicles?.length ? problem.vehicles[0].vid : undefined;
        const resource = this.solutionData.draftResourceMap[vehicleId];
        const resourceName = resource?.allocationName || resource?.vehicleName;
        const vehicleCapacity = problem.vehicles?.[0]?.constraints?.multiCapacity?.[0];

        problem?.tripInfoList.forEach((tripInfo: any, tripIndex: number) => {
          if (tripInfo.tripTotals) {
            const tripTotals = tripInfo.tripTotals;
            const sizeUtilization = tripTotals.sizeUtilization || 0;
            const weightUtilization = tripTotals.weightUtilization || 0;

            // tripTotals.totalSize/totalWeight can be miscalculated by the solver
            // (cargo attributed to the wrong trip at trip boundaries), while
            // sizeUtilization/weightUtilization are derived correctly from the
            // vehicle's declared capacity. Re-derive the absolute values from the
            // trustworthy utilization percentages instead of using totalSize/totalWeight directly.
            const tripVolume = vehicleCapacity?.size
              ? Math.round(sizeUtilization * vehicleCapacity.size)
              : (tripTotals.totalSize || 0);
            const tripWeight = vehicleCapacity?.weight
              ? Math.round(weightUtilization * vehicleCapacity.weight)
              : (tripTotals.totalWeight || 0);

            this.routesCapacityData.push({
              resourceName: resourceName,
              routeId: problemIndex,
              tripVolume: tripVolume,
              tripWeight: tripWeight,
              volumeUtilPercent: sizeUtilization * 100,
              weightUtilPercent: weightUtilization * 100,
              capacityHitPercent: this.calculateCapacityHit(
                sizeUtilization * 100,
                weightUtilization * 100
              ),
              dropsCount: tripTotals.customerVisits || 0,
              capacityUtilPass: null
            });
          }
        });
    });
  }

  private processRoutesTimeData() {
    this.routesTimeData = [];

    if (!this.solutionData || !this.solutionData.solution.problemSolvedList) {
      return;
    }

    // Iterate through problemSolvedList
    this.solutionData.solution.problemSolvedList.forEach((problem: any, problemIndex: number) => {
      if (problem.totals) {
        const totals = problem.totals;
        const availableTimeSec = totals.availableTimeSec || 0;
        const totalTimeSec = totals.totalTimeSec || 0;
        const totalTravelTimeSec = totals.totalTravelTimeSec || 0;
        const totalServiceTimeSec = totals.totalServiceTimeSec || 0;

        // Calculate percentages
        const timeUtilPercent = availableTimeSec > 0
          ? (totalTimeSec / availableTimeSec) * 100
          : 0;
        const travelSharePercent = totalTimeSec > 0
          ? (totalTravelTimeSec / totalTimeSec) * 100
          : 0;
        const serviceSharePercent = totalTimeSec > 0
          ? (totalServiceTimeSec / totalTimeSec) * 100
          : 0;

        const vehicleId = problem.vehicles?.length ? problem.vehicles[0].vid : undefined;
        const resource = this.solutionData.draftResourceMap[vehicleId];

        this.routesTimeData.push({
          resourceName: resource?.allocationName || resource?.vehicleName,
          routeId: problemIndex,
          resourceSpanMin: availableTimeSec / 60,
          plannedRouteTimeMin: totalTimeSec / 60,
          timeUtilPercent: timeUtilPercent,
          travelSharePercent: travelSharePercent,
          serviceSharePercent: serviceSharePercent,
          timeUtilPass: null
        });
      }
    });
  }

  private calculateCapacityHit(volumeUtil: number, weightUtil: number): number {
    return Math.max(volumeUtil, weightUtil);
  }



  private processRetripsData() {
    this.retripsData = [];

    if (!this.solutionData || !this.solutionData.solution.problemSolvedList) {
      return;
    }

    // Iterate through problemSolvedList
    this.solutionData.solution.problemSolvedList.forEach((problem: any, problemIndex: number) => {
      const tripsPerRoute = problem.totals?.numberOfTrips || 0;
      const maxTripDeclared =  null;
      const tripCountMatch = maxTripDeclared ? tripsPerRoute === maxTripDeclared : null;

      const vehicleId = problem.vehicles?.length ? problem.vehicles[0].vid : undefined;
      const resource = this.solutionData.draftResourceMap[vehicleId];

        problem?.tripInfoList.forEach((tripInfo: any, tripIndex: number) => {
          // Check if first stop is a depot
          const depotTurnarounds = this.checkDepotTurnarounds(tripInfo, problem);

          const tripTotals = tripInfo.tripTotals || {};
          const remainingVolume = tripTotals.leftoverSize || 0;
          const remainingWeight = tripTotals.leftoverWeight || 0;
          const depotReturnedWithLoad = remainingVolume > 0 || remainingWeight > 0;

          // Calculate min gap between trips (if available)
          const minGapBetweenTripsMin = tripInfo.minGapBetweenTrips
            ? tripInfo.minGapBetweenTrips / 60
            : 0;

          const retripsPass = this.isRetripsPass(
            tripCountMatch,
            depotTurnarounds,
            depotReturnedWithLoad
          );

          this.retripsData.push({
            resourceName: resource?.allocationName || resource?.vehicleName,
            routeId: problemIndex,
            tripsPerRoute: tripsPerRoute,
            maxTripDeclared: maxTripDeclared,
            tripCountMatch: tripCountMatch,
            depotTurnarounds: depotTurnarounds,
            remainingVolume: remainingVolume,
            remainingWeight: remainingWeight,
            depotReturnedWithLoad: depotReturnedWithLoad,
            minGapBetweenTripsMin: minGapBetweenTripsMin,
            retripsPass: retripsPass
          });
        });

    });
  }

  private checkDepotTurnarounds(tripInfo: any, problem: any): boolean {
    // Check if the first stop in stopOrder is a depot
    if (!tripInfo.stopOrder || tripInfo.stopOrder.length === 0) {
      return false;
    }

    const firstStopId = tripInfo.stopOrder[0];

    // Look up this stop in the tasks stopList
    if (problem.tasks && problem.tasks.stopList) {
      const stop = problem.tasks.stopList.find((s: any) => s.sid === firstStopId);
      if (stop && stop.locationType === 'DEPOT') {
        return true;
      }
    }

    return false;
  }

  private isRetripsPass(
    tripCountMatch: boolean | null,
    depotTurnarounds: boolean,
    depotReturnedWithLoad: boolean
  ): boolean {
    // Pass if:
    // - Trip count matches (if applicable)
    // - Has depot turnarounds
    // - Does NOT return to depot with load
    const tripCountOk = tripCountMatch === null || tripCountMatch === true;
    const noLoadAtReturn = !depotReturnedWithLoad;

    return tripCountOk && depotTurnarounds && noLoadAtReturn;
  }

  private processComplianceData() {
    this.complianceData = [];

    if (!this.solutionData || !this.solutionData.solution.problemSolvedList) {
      return;
    }


    // Iterate through problemSolvedList
    this.solutionData.solution.problemSolvedList.forEach((problem: any, problemIndex: number) => {
      if (problem.totals) {
        const totals = problem.totals;
        const plannedDutySec = totals.totalTimeSec || 0;
        const drivingSec = totals.totalTravelTimeSec || 0;

        // Get constraints from first vehicle
        const vehicle = problem.vehicles && problem.vehicles[0];
        const maxDurationMin = vehicle?.constraints?.maxDuration || null;
        const startTime = vehicle?.startTime || 0;
        const endTime = vehicle?.endTime || 0;

        // Check driving limit (maxDuration is in minutes, need to convert to seconds)
        const drivingLimitOk = maxDurationMin !== null
          ? drivingSec < (maxDurationMin * 60)
          : true;

        // Check shift window (times are in milliseconds, need to convert to seconds)
        const shiftWindowSec = endTime > startTime ? (endTime - startTime) / 1000 : 0;
        const shiftWindowOk = shiftWindowSec > 0
          ? drivingSec < shiftWindowSec
          : true;

        const dutyLimitOk = null;

        // Compliance passes if driving limit is ok (as per user specification)
        const compliancePass = drivingLimitOk;

        const vehicleId = problem.vehicles?.length ? problem.vehicles[0].vid : undefined;
        const resource = this.solutionData.draftResourceMap[vehicleId];

        this.complianceData.push({
          resourceName: resource?.allocationName || resource?.vehicleName,
          routeId: problemIndex,
          plannedDutySec: plannedDutySec,
          drivingSec: drivingSec,
          dutyLimitOk: dutyLimitOk,
          drivingLimitOk: drivingLimitOk,
          shiftWindowOk: shiftWindowOk,
          compliancePass: compliancePass
        });
      }
    });
  }

  private processEmptyMileageData() {
    this.emptyMileageData = [];

    if (!this.solutionData || !this.solutionData.solution.problemSolvedList) {
      return;
    }



    // Iterate through problemSolvedList
    this.solutionData.solution.problemSolvedList.forEach((problem: any, problemIndex: number) => {
      if (problem.totals) {
        const totals = problem.totals;
        const emptyDistanceMeter = totals.emptyTravelDistanceMeter || 0;
        const totalDistanceMeter = totals.totalTravelDistanceMeter || 0;

        // Convert meters to kilometers
        const emptyDistanceKm = emptyDistanceMeter / 1000;
        const totalDistanceKm = totalDistanceMeter / 1000;

        // Calculate empty share percentage
        const emptySharePercent = totalDistanceKm > 0
          ? (emptyDistanceKm / totalDistanceKm) * 100
          : 0;

        const emptyMileagePass = null

        const vehicleId = problem.vehicles?.length ? problem.vehicles[0].vid : undefined;
        const resource = this.solutionData.draftResourceMap[vehicleId];

        this.emptyMileageData.push({
          resourceName: resource?.allocationName || resource?.vehicleName,
          routeId: problemIndex,
          emptyDistanceKm: emptyDistanceKm,
          totalDistanceKm: totalDistanceKm,
          emptySharePercent: emptySharePercent,
          emptyMileagePass: emptyMileagePass
        });
      }
    });
  }

  private processTripDensityData() {
    this.tripDensityData = [];

    if (!this.solutionData || !this.solutionData.solution.problemSolvedList) {
      return;
    }

    // Iterate through problemSolvedList and tripInfoList
    this.solutionData.solution.problemSolvedList.forEach((problem: any, problemIndex: number) => {
      const vehicleId = problem.vehicles?.length ? problem.vehicles[0].vid : undefined;
      const resource = this.solutionData.draftResourceMap[vehicleId];

      problem?.tripInfoList.forEach((tripInfo: any) => {
        const tripTotals = tripInfo.tripTotals || {};

        const tripNumber = tripInfo.tripNumber || 0;
        const dropoffsCount = tripTotals.customerVisits || 0;
        const avgSpacingMeter = tripTotals.avgCustomerVisitsDistanceMeter || 0;
        const maxGapMeter = tripTotals.maxGapDistanceMeter || 0;

        // Convert meters to kilometers
        const avgSpacingKm = avgSpacingMeter / 1000;
        const maxGapKm = maxGapMeter / 1000;

        const medianSpacingKm = null;

        const tripDensityPass = null;

        this.tripDensityData.push({
          resourceName: resource?.allocationName || resource?.vehicleName,
          routeId: problemIndex,
          tripNumber: tripNumber,
          dropoffsCount: dropoffsCount,
          avgSpacingKm: avgSpacingKm,
          medianSpacingKm: medianSpacingKm,
          maxGapKm: maxGapKm,
          tripDensityPass: tripDensityPass
        });
      });
    });
  }

  private processGlobalViewData() {
    this.globalData = {
      totalAccounts: 0,
      passedTrue: 0,
      passedFalse: 0,
      pctPassed: 0,
      pctFailed: 0,
      sumTotalDistanceKm: 0,
      usedResources: 0,
      distancePerType: {}
    };

    if (!this.solutionData) {
      return;
    }

    if (this.accountData) {
      this.globalData.totalAccounts = this.accountData.length;
      this.accountData.forEach((account: any) => {
        if (account.passed === true) {
          this.globalData.passedTrue++;
        } else if (account.passed === false) {
          this.globalData.passedFalse++;
        }
      });

      if (this.globalData.totalAccounts > 0) {
        this.globalData.pctPassed = (this.globalData.passedTrue / this.globalData.totalAccounts) * 100;
        this.globalData.pctFailed = (this.globalData.passedFalse / this.globalData.totalAccounts) * 100;
      }
    }

    // Use top-level totals for aggregate metrics (more accurate)
    if (this.solutionData.solution && this.solutionData.solution.totals) {
      const topTotals = this.solutionData.solution.totals;
      
      // Use totalTravelDistanceMeter for ALL distance (includes empty miles)
      if (topTotals.totalTravelDistanceMeter) {
        this.globalData.sumTotalDistanceKm = topTotals.totalTravelDistanceMeter / 1000;
      }
    }

    // Calculate used resources - only count vehicles actually assigned to routes
    if (this.solutionData.solution && this.solutionData.solution.problemSolvedList) {
      const usedVehicleIds = new Set();
      
      this.solutionData.solution.problemSolvedList.forEach((problem: any) => {
        if (problem.vehicles && problem.vehicles.length > 0) {
          const vehicleId = problem.vehicles[0].vid;
          if (vehicleId) {
            usedVehicleIds.add(vehicleId);
          }
        }
      });
      
      this.globalData.usedResources = usedVehicleIds.size;
    }

    // Calculate distance per type - simplified approach using top-level data
    // Note: This is a placeholder - the actual implementation depends on data structure
    this.globalData.distancePerType = {
      'Delivery': this.globalData.sumTotalDistanceKm
    };
  }

  private processMeasurementSummaryData() {
    this.summaryData = [];

    if (!this.solutionData || !this.solutionData.solution.problemSolvedList) {
      return;
    }

    this.solutionData.solution.problemSolvedList.forEach((problem: any, problemIndex: number) => {
      const routeId = problemIndex;
      const vehicleId = problem.vehicles?.length ? problem.vehicles[0].vid : undefined;
      const resource = this.solutionData.draftResourceMap[vehicleId];
      const resourceName = resource?.allocationName || resource?.vehicleName;

      // Get ALL entries for this route (in case there are multiple trips)
      const capacityEntries = this.routesCapacityData.filter((item: any) =>
        item.routeId === routeId
      );

      const retripsEntries = this.retripsData.filter((item: any) =>
        item.routeId === routeId
      );

      const tripDensityEntries = this.tripDensityData.filter((item: any) =>
        item.routeId === routeId
      );

      // Get single entries (one per route)
      const timeData = this.routesTimeData.find((item: any) =>
        item.routeId === routeId
      );

      const complianceDataItem = this.complianceData.find((item: any) =>
        item.routeId === routeId
      );

      const emptyMileageDataItem = this.emptyMileageData.find((item: any) =>
        item.routeId === routeId
      );

      // Aggregate trip-level data
      // Capacity: Use MAX (worst-case capacity hit)
      const capacityHitPercent = capacityEntries.length > 0
        ? Math.max(...capacityEntries.map(e => e.capacityHitPercent))
        : 0;
      
      // Capacity pass: ALL trips must pass (use AND logic)
      const capacityUtilPass = capacityEntries.length > 0
        ? capacityEntries.every(e => e.capacityUtilPass === true) ? true : 
          capacityEntries.some(e => e.capacityUtilPass === false) ? false : null
        : null;

      // Retrips pass: ALL trips must pass
      const retripsPass = retripsEntries.length > 0
        ? retripsEntries.every(e => e.retripsPass === true) ? true :
          retripsEntries.some(e => e.retripsPass === false) ? false : null
        : null;

      // Trip density: Average spacing across all trips
      const avgSpacingKm = tripDensityEntries.length > 0
        ? tripDensityEntries.reduce((sum, e) => sum + (e.avgSpacingKm || 0), 0) / tripDensityEntries.length
        : 0;

      // Trip density pass: ALL trips must pass
      const tripDensityPass = tripDensityEntries.length > 0
        ? tripDensityEntries.every(e => e.tripDensityPass === true) ? true :
          tripDensityEntries.some(e => e.tripDensityPass === false) ? false : null
        : null;

      // Single-entry metrics (already one per route)
      const timeUtilPercent = timeData?.timeUtilPercent || 0;
      const timeUtilPass = timeData?.timeUtilPass || null;
      const compliancePass = complianceDataItem?.compliancePass || null;
      const emptySharePercent = emptyMileageDataItem?.emptySharePercent || 0;
      const emptyMileagePass = emptyMileageDataItem?.emptyMileagePass || null;

      // Calculate overall pass: TRUE only if ALL non-null passes are TRUE
      const allPasses = [
        capacityUtilPass,
        timeUtilPass,
        retripsPass,
        compliancePass,
        emptyMileagePass,
        tripDensityPass
      ];

      const nonNullPasses = allPasses.filter(pass => pass !== null);
      const overallPass = nonNullPasses.length > 0
        ? nonNullPasses.every(pass => pass === true)
        : null;

      this.summaryData.push({
        resourceName: resourceName,
        routeId: routeId,
        capacityHitPercent: capacityHitPercent,
        capacityUtilPass: capacityUtilPass,
        timeUtilPercent: timeUtilPercent,
        timeUtilPass: timeUtilPass,
        retripsPass: retripsPass,
        compliancePass: compliancePass,
        emptySharePercent: emptySharePercent,
        emptyMileagePass: emptyMileagePass,
        avgSpacingKm: avgSpacingKm,
        tripDensityPass: tripDensityPass,
        overallPass: overallPass
      });
    });
  }




}

angular
  .module('bringoz.admin.planning')
  .component('kpiContent', {
    template: require('./kpi-content.html').default,
    controller: KpiContentComponent,
    bindings: {
      selectedSection: '<',
      accountData: '<',
      solutionData: '<'
    }
  });
